import {
  ForgotPasswordConfirm,
  ForgotPasswordRequest,
  NewPasswordLogin,
  TotpCodeLogin,
  TotpSetupLogin,
  UsernamePasswordLogin,
  useToastNotifications,
} from "@/shared/coralreef";
import { viewWidth } from "csx";
import { Content } from "gls/lib";
import { useState } from "react";
import type { SignInResult } from "../../shell-handle/auth";

const NEW_PASSWORD_REQUIRED = "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED";
const TOTP_CODE_REQUIRED = "CONFIRM_SIGN_IN_WITH_TOTP_CODE";
const TOTP_SETUP_REQUIRED = "CONTINUE_SIGN_IN_WITH_TOTP_SETUP";
const RESET_PASSWORD_REQUIRED = "RESET_PASSWORD";
const CONFIRM_RESET_WITH_CODE = "CONFIRM_RESET_PASSWORD_WITH_CODE";

type Step =
  | "login"
  | "new-password"
  | "totp-code"
  | "totp-setup"
  | "cancelling"
  | "forgot-request"
  | "forgot-confirm";

type TotpSetup = { secret: string; via: "challenge" | "enrolment" };

export interface LoginPageProps {
  signIn(email: string, password: string): Promise<SignInResult>;
  confirmSignIn(challengeResponse: string): Promise<SignInResult>;
  isTotpEnrolmentRequired(): Promise<boolean>;
  startTotpSetup(): Promise<string>;
  completeTotpSetup(code: string): Promise<void>;
  abandonSignIn(): Promise<void>;
  resetPassword(
    username: string,
  ): Promise<{ nextStep?: { resetPasswordStep: string } }>;
  confirmResetPassword(
    username: string,
    confirmationCode: string,
    newPassword: string,
  ): Promise<void>;
  onSuccess(): void | Promise<void>;
}

export function LoginPage({
  signIn,
  confirmSignIn,
  isTotpEnrolmentRequired,
  startTotpSetup,
  completeTotpSetup,
  abandonSignIn,
  resetPassword,
  confirmResetPassword,
  onSuccess,
}: LoginPageProps) {
  const { addToast } = useToastNotifications();
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("login");
  const [resetUsername, setResetUsername] = useState("");
  const [totpSetup, setTotpSetup] = useState<TotpSetup | null>(null);

  const goTo = (next: Step) => {
    setError(null);
    setInfo(null);
    setStep(next);
  };

  const toastOnFailure = (work: Promise<unknown>, fallback: string) => {
    work.catch((e) => {
      addToast(e instanceof Error ? e.message : fallback, "error");
    });
  };

  const abandon = async (e: unknown): Promise<never> => {
    await abandonSignIn();
    goTo("login");
    throw e;
  };

  const finishSignIn = async () => {
    let secret: string | null = null;
    try {
      if (await isTotpEnrolmentRequired()) secret = await startTotpSetup();
    } catch (e) {
      return abandon(e);
    }
    if (secret) {
      setTotpSetup({ secret, via: "enrolment" });
      goTo("totp-setup");
      return;
    }
    await onSuccess();
  };

  const handleSignInResult = async (r: SignInResult) => {
    if (r.isSignedIn) return finishSignIn();
    const signInStep = r.nextStep?.signInStep;
    if (signInStep === NEW_PASSWORD_REQUIRED) {
      goTo("new-password");
    } else if (signInStep === TOTP_CODE_REQUIRED) {
      goTo("totp-code");
    } else if (signInStep === TOTP_SETUP_REQUIRED && r.nextStep?.totpSecret) {
      setTotpSetup({ secret: r.nextStep.totpSecret, via: "challenge" });
      goTo("totp-setup");
    } else if (signInStep === RESET_PASSWORD_REQUIRED) {
      goTo("forgot-request");
      setInfo("Your password must be reset before you can log in.");
    } else {
      return abandon(new Error(`Unsupported sign-in step: ${signInStep}`));
    }
  };

  const backToLogin = () => {
    goTo("cancelling");
    toastOnFailure(
      abandonSignIn().finally(() => {
        setTotpSetup(null);
        goTo("login");
      }),
      "Failed to cancel sign-in",
    );
  };

  const onLoginPasswordSubmitHandler = (username: string, password: string) => {
    toastOnFailure(
      signIn(username, password).then(handleSignInResult),
      "Sign-in failed",
    );
  };

  const onNewPasswordSubmitHandler = (newPassword: string) => {
    toastOnFailure(
      confirmSignIn(newPassword).then(handleSignInResult),
      "Failed to set new password",
    );
  };

  const onTotpCodeSubmitHandler = (code: string) => {
    toastOnFailure(
      confirmSignIn(code).then(handleSignInResult),
      "Failed to verify code",
    );
  };

  const onTotpSetupSubmitHandler = (code: string) => {
    const work =
      totpSetup?.via === "enrolment"
        ? completeTotpSetup(code).then(() => onSuccess())
        : confirmSignIn(code).then(handleSignInResult);
    toastOnFailure(work, "Failed to set up authenticator");
  };

  const onForgotRequestSubmitHandler = (username: string) => {
    setError(null);
    resetPassword(username)
      .then((r) => {
        if (r.nextStep?.resetPasswordStep === CONFIRM_RESET_WITH_CODE) {
          setResetUsername(username);
          goTo("forgot-confirm");
        } else {
          setInfo("Your password has been reset. Please log in.");
          setStep("login");
        }
      })
      .catch((e) => {
        setError(
          e instanceof Error ? e.message : "Failed to start password reset",
        );
      });
  };

  const onForgotConfirmSubmitHandler = (
    confirmationCode: string,
    newPassword: string,
  ) => {
    setError(null);
    confirmResetPassword(resetUsername, confirmationCode, newPassword)
      .then(() => {
        setInfo("Your password has been reset. Please log in.");
        setStep("login");
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : "Failed to reset password");
      });
  };

  return (
    <Content
      width={viewWidth(100)}
      height="75vh"
      verticalAlign="center"
      horizontalAlign="center"
    >
      <div className="flex flex-col items-center gap-3">
        {step === "login" && (
          <UsernamePasswordLogin
            onSubmit={onLoginPasswordSubmitHandler}
            onForgotPassword={() => goTo("forgot-request")}
          />
        )}
        {step === "new-password" && (
          <NewPasswordLogin onSubmit={onNewPasswordSubmitHandler} />
        )}
        {step === "totp-code" && (
          <TotpCodeLogin
            onSubmit={onTotpCodeSubmitHandler}
            onBackToLogin={backToLogin}
          />
        )}
        {step === "totp-setup" && totpSetup && (
          <TotpSetupLogin
            secret={totpSetup.secret}
            onSubmit={onTotpSetupSubmitHandler}
            onBackToLogin={backToLogin}
          />
        )}
        {step === "forgot-request" && (
          <ForgotPasswordRequest
            onSubmit={onForgotRequestSubmitHandler}
            onBackToLogin={() => goTo("login")}
          />
        )}
        {step === "forgot-confirm" && (
          <ForgotPasswordConfirm
            onSubmit={onForgotConfirmSubmitHandler}
            onBackToLogin={() => goTo("login")}
          />
        )}
        {info && (
          <div
            role="status"
            data-testid="info-message"
            className="text-sm w-[300px]"
          >
            {info}
          </div>
        )}
        {error && (
          <div
            role="alert"
            data-testid="error-message"
            className="text-red-500 text-sm w-[300px]"
          >
            {error}
          </div>
        )}
      </div>
    </Content>
  );
}
